import { useEffect, useRef, useState } from 'react'
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Share2,
  Heart,
  Music,
  Clock,
  MessageCircle,
  Moon,
  Send,
  Flame,
  Smile,
  X,
  Award,
  Calendar,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react'

import { useNavigate } from 'react-router-dom'
import { toast } from '../hooks/useToast'
import DedicaceSpotlight from '../components/Dedicaces/DedicaceSpotlight'
import { useCurrentDedicaces } from '../hooks/useCurrentDedicaces'
import { vibrate, VERSION_LABEL } from '../utils/appPrefs'
import { db } from '../firebase/config'

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  onSnapshot,
  addDoc,
  updateDoc,
  increment,
  serverTimestamp,
  orderBy,
  limit
} from 'firebase/firestore'

const RADIO_TRACK_URL =
  'https://api.radioking.io/widget/radio/radio-johnny-hallyday/track/current'

const SITE_URL =
  'https://100pour100johnny.com'

function RadioPage({
  currentUser,
  globalAudio
}) {
  const navigate = useNavigate()
  const {
    isPlaying,
    setIsPlaying,
    volume,
    setVolume,
    isMuted,
    setIsMuted,
    audioRef,
    togglePlay
  } = globalAudio

  // ============================================================
  // RADIO
  // ============================================================

  const [currentTrack, setCurrentTrack] =
    useState({
      title: "Que Je T'aime",
      artist: 'Johnny Hallyday',
      cover: null
    })

  const [duration, setDuration] =
    useState('00:00')

  // Aucun faux nombre d'auditeurs.
  // ============================================================

  // ============================================================
  // DÉDICACES
  // ============================================================

  const [recentDedicaces, setRecentDedicaces] =
    useState([])


  // ============================================================
  // PANNEAUX
  // ============================================================

  const [activePanel, setActivePanel] =
    useState(null)

  // ============================================================
  // MINUTEUR
  // ============================================================

  const [sleepTimer, setSleepTimer] =
    useState(null)

  const [sleepTimeRemaining, setSleepTimeRemaining] =
    useState(0)

  const sleepTimerRef =
    useRef(null)

  // ============================================================
  // CHAT
  // ============================================================

  const [chatMessages, setChatMessages] =
    useState([])

  const [chatInput, setChatInput] =
    useState('')

  // ============================================================
  // RÉACTIONS
  // ============================================================

  const [reactions, setReactions] =
    useState({
      fire: 0,
      heart: 0,
      love: 0,
      music: 0
    })

  const [floatingEmojis, setFloatingEmojis] =
    useState([])

  // ============================================================
  // PROFIL
  // ============================================================

  const [userProfile, setUserProfile] =
    useState({
      totalListenTime: 0,
      badges: [],
      streak: 0,
      sessionsCount: 0
    })

  // ============================================================
  // VOTES
  // ============================================================

  const [hasTopped, setHasTopped] =
    useState(false)

  const [hasFlopped, setHasFlopped] =
    useState(false)

  const [songVotes, setSongVotes] =
    useState({
      tops: 0,
      flops: 0
    })

  const [isVoting, setIsVoting] =
    useState(false)

  // ============================================================
  // SESSION
  // ============================================================

  const startTimeRef =
    useRef(null)

  const listenIntervalRef =
    useRef(null)

  const previousTrackRef =
    useRef(null)

  // ============================================================
  // PANNEAUX
  // ============================================================

  const togglePanel = panel => {
    setActivePanel(
      current =>
        current === panel
          ? null
          : panel
    )
  }

  // ============================================================
  // RÉCUPÉRATION DE LA CHANSON
  // ============================================================

  useEffect(() => {
    let cancelled = false

    const fetchCurrentTrack =
      async () => {
        try {
          const response =
            await fetch(
              RADIO_TRACK_URL
            )

          if (!response.ok) {
            throw new Error(
              `HTTP ${response.status}`
            )
          }

          const data =
            await response.json()

          if (
            cancelled ||
            !data?.title
          ) {
            return
          }

          setCurrentTrack({
            title:
              data.title,
            artist:
              data.artist ||
              'Johnny Hallyday',
            cover:
              data.cover ||
              data.artwork ||
              null
          })
        } catch (error) {
          console.error(
            'Erreur récupération chanson:',
            error
          )
        }
      }

    fetchCurrentTrack()

    const interval =
      setInterval(
        fetchCurrentTrack,
        10000
      )

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  // ============================================================
  // NOTIFICATION NOUVELLE CHANSON
  // ============================================================

  useEffect(() => {
    if (
      !currentTrack?.title
    ) {
      return
    }

    if (
      !previousTrackRef.current
    ) {
      previousTrackRef.current =
        currentTrack

      return
    }

    previousTrackRef.current =
      currentTrack
  }, [
    currentTrack,
    isPlaying
  ])

  // ============================================================
  // DÉDICACES EN COURS (approuvées, non archivées)
  // ============================================================
  const currentDedicaces = useCurrentDedicaces(20)
  useEffect(() => { setRecentDedicaces(currentDedicaces) }, [currentDedicaces])

  // ============================================================
  // CHAT
  // ============================================================

  useEffect(() => {
    let unsubscribe =
      () => {}

    try {
      const chatQuery =
        query(
          collection(
            db,
            '100johnny',
            'main',
            'chatMessages'
          ),
          orderBy(
            'createdAt',
            'desc'
          ),
          limit(50)
        )

      unsubscribe =
        onSnapshot(
          chatQuery,
          snapshot => {
            const messages =
              snapshot.docs
                .map(item => ({
                  id:
                    item.id,
                  ...item.data()
                }))
                .reverse()

            setChatMessages(
              messages
            )
          },
          error => {
            console.error(
              'Erreur chat:',
              error
            )
          }
        )
    } catch (error) {
      console.error(
        'Erreur connexion chat:',
        error
      )
    }

    return () =>
      unsubscribe()
  }, [])

  const sendChatMessage =
    async event => {
      event.preventDefault()

      if (!currentUser) {
        toast.error(
          '🔒 Connectez-vous pour chatter'
        )

        return
      }

      const message =
        chatInput.trim()

      if (!message) {
        return
      }

      try {
        await addDoc(
          collection(
            db,
            '100johnny',
            'main',
            'chatMessages'
          ),
          {
            userId:
              currentUser.uid,
            userName:
              currentUser.displayName ||
              currentUser.email?.split(
                '@'
              )[0] ||
              'Anonyme',
            message,
            createdAt:
              serverTimestamp()
          }
        )

        setChatInput('')
      } catch (error) {
        console.error(
          'Erreur envoi chat:',
          error
        )

        toast.error(
          "❌ Erreur lors de l'envoi"
        )
      }
    }

  // ============================================================
  // RÉACTIONS
  // ============================================================

  useEffect(() => {
    const reactionsRef =
      doc(
        db,
        '100johnny',
        'main',
        'globalStats',
        'reactions'
      )

    const unsubscribe =
      onSnapshot(
        reactionsRef,
        snapshot => {
          if (
            !snapshot.exists()
          ) {
            return
          }

          const data =
            snapshot.data()

          setReactions({
            fire:
              data.fire || 0,
            heart:
              data.heart || 0,
            love:
              data.love || 0,
            music:
              data.music || 0
          })
        },
        error => {
          console.error(
            'Erreur réactions:',
            error
          )
        }
      )

    return () =>
      unsubscribe()
  }, [])

  const addReaction =
    async type => {
      if (!currentUser) {
        toast.error(
          '🔒 Connectez-vous pour réagir'
        )
        return
      }

      const allowed = [
        'fire',
        'heart',
        'love',
        'music'
      ]

      if (
        !allowed.includes(type)
      ) {
        return
      }

      try {
        await setDoc(
          doc(
            db,
            '100johnny',
            'main',
            'globalStats',
            'reactions'
          ),
          {
            [type]:
              increment(1),
            updatedAt:
              serverTimestamp()
          },
          {
            merge: true
          }
        )

        const emojis = {
          fire: '🔥',
          heart: '❤️',
          love: '😍',
          music: '🎸'
        }

        const item = {
          id:
            `${Date.now()}-${Math.random()}`,
          emoji:
            emojis[type],
          left:
            25 +
            Math.random() *
              50
        }

        setFloatingEmojis(
          current => [
            ...current,
            item
          ]
        )

        setTimeout(() => {
          setFloatingEmojis(
            current =>
              current.filter(
                value =>
                  value.id !==
                  item.id
              )
          )
        }, 2200)
      } catch (error) {
        console.error(
          'Erreur réaction:',
          error
        )
      }
    }

  // ============================================================
  // PROFIL
  // ============================================================

  useEffect(() => {
    if (!currentUser) {
      return undefined
    }

    const userRef =
      doc(
        db,
        'users',
        currentUser.uid
      )

    const unsubscribe =
      onSnapshot(
        userRef,
        async snapshot => {
          if (
            snapshot.exists()
          ) {
            const data =
              snapshot.data()

            const totalListenTime = Math.max(
              Number(data.totalListenTime || 0),
              Number(data.totalListeningTime || 0),
              Number(data.totalSecondsWatched || 0),
              Number(data.stats?.totalSecondsWatched || 0)
            )

            setUserProfile({
              totalListenTime,
              badges:
                data.badges || [],
              streak:
                data.streak || data.currentStreak || data.stats?.currentStreak || 0,
              sessionsCount:
                data.sessionsCount || data.sessions || data.stats?.sessionsCount || 0
            })

            return
          }

          try {
            await setDoc(
              userRef,
              {
                totalListenTime: 0,
                badges: [],
                streak: 1,
                sessionsCount: 1,
                createdAt:
                  serverTimestamp(),
                lastVisit:
                  serverTimestamp()
              }
            )
          } catch (error) {
            console.error(
              'Erreur création profil:',
              error
            )
          }
        },
        error => {
          console.error(
            'Erreur profil:',
            error
          )
        }
      )

    return () =>
      unsubscribe()
  }, [currentUser])

  // ============================================================
  // TEMPS D'ÉCOUTE
  // Ce suivi vit maintenant dans App.jsx (persiste même quand on
  // quitte cet onglet), plus ici — voir App.jsx pour l'explication.
  // ============================================================

  // ============================================================
  // DURÉE DE SESSION
  // ============================================================

  useEffect(() => {
    if (!isPlaying) {
      setDuration('00:00')

      return undefined
    }

    let start

    try {
      start =
        localStorage.getItem(
          'sessionStartTime'
        )

      if (!start) {
        start =
          Date.now().toString()

        localStorage.setItem(
          'sessionStartTime',
          start
        )
      }
    } catch {
      start =
        Date.now().toString()
    }

    startTimeRef.current =
      Number(start)

    const interval =
      setInterval(() => {
        const elapsed =
          Math.max(
            0,
            Math.floor(
              (Date.now() -
                startTimeRef.current) /
                1000
            )
          )

        const minutes =
          Math.floor(
            elapsed / 60
          )

        const seconds =
          elapsed % 60

        setDuration(
          `${String(
            minutes
          ).padStart(
            2,
            '0'
          )}:${String(
            seconds
          ).padStart(
            2,
            '0'
          )}`
        )
      }, 1000)

    return () =>
      clearInterval(interval)
  }, [isPlaying])

  // ============================================================
  // VOTES
  // ============================================================

  const songId =
    currentTrack.title
      ? currentTrack.title
          .toLowerCase()
          .replace(
            /[^a-z0-9]+/g,
            '-'
          )
          .replace(
            /^-|-$/g,
            ''
          )
      : null

  useEffect(() => {
    if (!songId) {
      setSongVotes({
        tops: 0,
        flops: 0
      })

      return undefined
    }

    const songRef =
      doc(
        db,
        '100johnny',
        'main',
        'songs',
        songId
      )

    const unsubscribe =
      onSnapshot(
        songRef,
        snapshot => {
          if (
            !snapshot.exists()
          ) {
            setSongVotes({
              tops: 0,
              flops: 0
            })

            return
          }

          const data =
            snapshot.data()

          setSongVotes({
            tops:
              data.tops || 0,
            flops:
              data.flops || 0
          })
        },
        error => {
          console.error(
            'Erreur votes:',
            error
          )
        }
      )

    return () =>
      unsubscribe()
  }, [songId])

  useEffect(() => {
    if (
      !currentUser ||
      !songId
    ) {
      setHasTopped(false)
      setHasFlopped(false)

      return
    }

    const voteQuery =
      query(
        collection(
          db,
          '100johnny',
          'main',
          'songVotes'
        ),
        where(
          'userId',
          '==',
          currentUser.uid
        ),
        where(
          'songId',
          '==',
          songId
        ),
        limit(20)
      )

    getDocs(voteQuery)
      .then(snapshot => {
        let top = false
        let flop = false

        snapshot.forEach(
          item => {
            const data =
              item.data()

            if (
              data.type ===
              'top'
            ) {
              top = true
            }

            if (
              data.type ===
              'flop'
            ) {
              flop = true
            }
          }
        )

        setHasTopped(top)
        setHasFlopped(flop)
      })
      .catch(error => {
        console.error(
          'Erreur récupération votes:',
          error
        )
      })
  }, [
    currentUser,
    songId
  ])

  const handleVote =
    async type => {
      if (!currentUser) {
        toast.error(
          '🔒 Connectez-vous pour voter'
        )

        return
      }

      if (!songId) {
        return
      }

      if (
        type !== 'top' &&
        type !== 'flop'
      ) {
        return
      }

      if (isVoting) {
        return
      }

      if (
        type === 'top' &&
        hasTopped
      ) {
        return
      }

      if (
        type === 'flop' &&
        hasFlopped
      ) {
        return
      }

      setIsVoting(true)

      try {
        await addDoc(
          collection(
            db,
            '100johnny',
            'main',
            'songVotes'
          ),
          {
            userId:
              currentUser.uid,
            songId,
            type,
            songTitle:
              currentTrack.title,
            songArtist:
              currentTrack.artist,
            createdAt:
              serverTimestamp()
          }
        )

        await setDoc(
          doc(
            db,
            '100johnny',
            'main',
            'songs',
            songId
          ),
          {
            title:
              currentTrack.title,
            artist:
              currentTrack.artist,
            [type === 'top'
              ? 'tops'
              : 'flops']:
              increment(1),
            updatedAt:
              serverTimestamp()
          },
          {
            merge: true
          }
        )

        if (type === 'top') {
          setHasTopped(true)
        } else {
          setHasFlopped(true)
        }

        toast.success(
          type === 'top'
            ? '👍 TOP enregistré'
            : '👎 FLOP enregistré'
        )
      } catch (error) {
        console.error(
          'Erreur vote:',
          error
        )

        toast.error(
          '❌ Erreur lors du vote'
        )
      } finally {
        setIsVoting(false)
      }
    }

  // ============================================================
  // MINUTEUR
  // ============================================================

  const startSleepTimer =
    minutes => {
      if (
        sleepTimerRef.current
      ) {
        clearInterval(
          sleepTimerRef.current
        )
      }

      setSleepTimer(minutes)
      setSleepTimeRemaining(
        minutes * 60
      )

      sleepTimerRef.current =
        setInterval(() => {
          setSleepTimeRemaining(
            previous => {
              if (
                previous <= 1
              ) {
                clearInterval(
                  sleepTimerRef.current
                )

                sleepTimerRef.current =
                  null

                if (
                  audioRef?.current
                ) {
                  audioRef.current.pause()
                }

                setIsPlaying(false)
                setSleepTimer(null)

                toast.success(
                  '😴 Radio arrêtée'
                )

                return 0
              }

              return previous - 1
            }
          )
        }, 1000)

      setActivePanel(null)

      toast.success(
        `⏰ Arrêt dans ${minutes} min`
      )
    }

  const cancelSleepTimer =
    () => {
      if (
        sleepTimerRef.current
      ) {
        clearInterval(
          sleepTimerRef.current
        )

        sleepTimerRef.current =
          null
      }

      setSleepTimer(null)
      setSleepTimeRemaining(0)

      toast.success(
        '⏰ Minuteur annulé'
      )
    }

  // ============================================================
  // VOLUME
  // ============================================================

  const handleVolumeChange =
    event => {
      const value =
        Math.max(
          0,
          Math.min(
            100,
            Number(
              event.target.value
            )
          )
        )

      setVolume(value)
      setIsMuted(
        value === 0
      )

      if (
        audioRef?.current
      ) {
        audioRef.current.volume =
          value / 100
      }
    }

  const handleMute =
    () => {
      if (
        audioRef?.current
      ) {
        if (isMuted) {
          audioRef.current.volume =
            volume / 100

          setIsMuted(false)
        } else {
          audioRef.current.volume =
            0

          setIsMuted(true)
        }

        return
      }

      setIsMuted(
        previous =>
          !previous
      )
    }

  // ============================================================
  // PARTAGE
  // ============================================================

  const shareCurrentTrack =
    async () => {
      const text =
        `🎵 J'écoute "${currentTrack.title}" de ${currentTrack.artist} sur Radio 100% Johnny !`

      try {
        if (
          navigator.share
        ) {
          await navigator.share({
            title:
              'Radio 100% Johnny',
            text,
            url:
              SITE_URL
          })

          return
        }

        if (
          navigator.clipboard
        ) {
          await navigator.clipboard.writeText(
            `${text}\n${SITE_URL}`
          )

          toast.success(
            '📋 Lien copié !'
          )

          return
        }

        throw new Error(
          'Partage indisponible'
        )
      } catch (error) {
        if (
          error?.name ===
          'AbortError'
        ) {
          return
        }

        toast.error(
          '❌ Partage impossible'
        )
      }
    }

  // ============================================================
  // FORMATAGE
  // ============================================================

  const formatSleepTime =
    seconds => {
      const minutes =
        Math.floor(
          seconds / 60
        )

      const secs =
        seconds % 60

      return `${minutes}:${String(
        secs
      ).padStart(
        2,
        '0'
      )}`
    }

  const formatTotalTime =
    seconds => {
      const value =
        Math.max(
          0,
          Number(seconds) ||
            0
        )

      const hours =
        Math.floor(
          value / 3600
        )

      const minutes =
        Math.floor(
          (value % 3600) /
            60
        )

      if (hours > 0) {
        return `${hours}h${
          minutes > 0
            ? ` ${minutes}min`
            : ''
        }`
      }

      if (minutes > 0) {
        return `${minutes}min`
      }

      return `${value}s`
    }

  // ============================================================
  // NETTOYAGE
  // ============================================================

  useEffect(() => {
    return () => {
      if (
        sleepTimerRef.current
      ) {
        clearInterval(
          sleepTimerRef.current
        )
      }

      if (
        listenIntervalRef.current
      ) {
        clearInterval(
          listenIntervalRef.current
        )
      }
    }
  }, [])

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="radio-page rp">
      <div className="rp-inner">

        {/* ============ EN-TÊTE ============ */}
        <header className="rp-top">
          <span className="rp-live">
            <span className="live-dot" />
            En direct
          </span>
          {isPlaying && (
            <span className="rp-session">
              <Clock size={15} />
              {duration}
            </span>
          )}
        </header>

        {/* ============ LECTEUR ============ */}
        <section className={`rp-hero ${isPlaying ? 'is-playing' : ''}`}>
          <div className="rp-cover">
            {currentTrack.cover ? (
              <img src={currentTrack.cover} alt="" />
            ) : (
              <div className="rp-cover__empty">
                <Music size={72} color="#d4af37" />
              </div>
            )}
          </div>

          <h1 className="rp-title">{currentTrack.title}</h1>
          <p className="rp-artist">{currentTrack.artist}</p>

          <button
            type="button"
            onClick={() => { vibrate(15); togglePlay() }}
            className="rp-play main-play"
            aria-label={isPlaying ? 'Mettre en pause' : 'Écouter la radio'}
          >
            {isPlaying
              ? <Pause size={44} fill="currentColor" strokeWidth={0} />
              : <Play size={44} fill="currentColor" strokeWidth={0} style={{ marginLeft: 6 }} />}
          </button>
          <div className="rp-play-hint">
            {isPlaying ? 'En écoute' : 'Appuyez pour écouter'}
          </div>

          <div className="rp-volume volume-control">
            <button
              type="button"
              className="rp-volume__mute"
              onClick={handleMute}
              aria-label={isMuted ? 'Activer le son' : 'Couper le son'}
              style={{ color: isMuted ? '#e0414a' : undefined }}
            >
              {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
            </button>
            <input
              type="range"
              min="0"
              max="100"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              aria-label="Volume"
              className="rp-range"
              style={{ '--val': `${isMuted ? 0 : volume}%` }}
            />
            <span className="rp-volume__val">{isMuted ? 0 : volume}%</span>
          </div>

          <div className="rp-votes">
            <TrackButton
              active={hasTopped}
              disabled={isVoting || hasTopped}
              onClick={() => handleVote('top')}
              label="J'aime ce titre"
            >
              <ThumbsUp size={20} />
              {songVotes.tops}
            </TrackButton>
            <TrackButton
              active={hasFlopped}
              disabled={isVoting || hasFlopped}
              onClick={() => handleVote('flop')}
              label="Je n'aime pas ce titre"
            >
              <ThumbsDown size={20} />
              {songVotes.flops}
            </TrackButton>
            <TrackButton onClick={shareCurrentTrack} label="Partager ce titre">
              <Share2 size={20} />
              Partager
            </TrackButton>
          </div>
        </section>

        {/* ============ DÉDICACES ============ */}
        <DedicaceSpotlight
          dedicaces={recentDedicaces}
          onSeeAll={() => navigate('/dedicaces')}
        />

        {/* ============ ACTIONS ============ */}
        <div className="rp-actions radio-action-menu">
          <ActionButton
            active={activePanel === 'chat'}
            icon={<MessageCircle />}
            label="Chat"
            badge={chatMessages.length > 0 ? chatMessages.length : null}
            onClick={() => togglePanel('chat')}
          />
          <ActionButton
            active={activePanel === 'reactions'}
            icon={<Smile />}
            label="Réactions"
            onClick={() => togglePanel('reactions')}
          />
          <ActionButton
            active={activePanel === 'sleep' || Boolean(sleepTimer)}
            icon={<Moon />}
            label="Sommeil"
            onClick={() => togglePanel('sleep')}
          />
          <ActionButton
            active={activePanel === 'profile'}
            icon={<Award />}
            label="Badges"
            onClick={() => togglePanel('profile')}
          />
          <ActionButton
            active={activePanel === 'calendar'}
            icon={<Calendar />}
            label="Johnny"
            onClick={() => togglePanel('calendar')}
          />
        </div>

        {/* ================================================== */}
        {/* CHAT                                             */}
        {/* ================================================== */}

        {activePanel ===
          'chat' && (
          <Panel>
            <PanelHeader
              icon={
                <MessageCircle />
              }
              title="Chat en direct"
              onClose={() =>
                setActivePanel(
                  null
                )
              }
            />

            <div
              style={{
                height:
                  300,
                overflowY:
                  'auto',
                padding:
                  14,
                borderRadius:
                  12,
                background:
                  '#080808',
                display:
                  'flex',
                flexDirection:
                  'column',
                gap:
                  10
              }}
            >
              {chatMessages.length ===
              0 ? (
                <p
                  style={{
                    margin:
                      'auto',
                    color:
                      '#777',
                    textAlign:
                      'center'
                  }}
                >
                  💬 Soyez le premier à parler !
                </p>
              ) : (
                chatMessages.map(
                  message => (
                    <div
                      key={
                        message.id
                      }
                      style={{
                        maxWidth:
                          '80%',
                        alignSelf:
                          message.userId ===
                          currentUser?.uid
                            ? 'flex-end'
                            : 'flex-start',
                        padding:
                          '10px 13px',
                        borderRadius:
                          12,
                        background:
                          message.userId ===
                          currentUser?.uid
                            ? 'rgba(0,217,255,.12)'
                            : 'rgba(255,255,255,.06)'
                      }}
                    >
                      <div
                        style={{
                          marginBottom:
                            4,
                          color:
                            '#00d9ff',
                          fontSize:
                            '.72rem',
                          fontWeight:
                            700
                        }}
                      >
                        {message.userName ||
                          'Anonyme'}
                      </div>

                      <div
                        style={{
                          color:
                            '#eee',
                          fontSize:
                            '.88rem'
                        }}
                      >
                        {message.message}
                      </div>
                    </div>
                  )
                )
              )}
            </div>

            {currentUser ? (
              <form
                onSubmit={
                  sendChatMessage
                }
                style={{
                  display:
                    'flex',
                  gap:
                    8,
                  marginTop:
                    10
                }}
              >
                <input
                  type="text"
                  value={
                    chatInput
                  }
                  onChange={event =>
                    setChatInput(
                      event.target
                        .value
                    )
                  }
                  placeholder="Écrivez un message..."
                  maxLength={200}
                  style={{
                    flex:
                      1,
                    minWidth:
                      0,
                    padding:
                      '12px 14px',
                    borderRadius:
                      10,
                    border:
                      '1px solid rgba(255,255,255,.12)',
                    background:
                      '#181818',
                    color:
                      '#fff',
                    outline:
                      'none'
                  }}
                />

                <button
                  type="submit"
                  disabled={
                    !chatInput.trim()
                  }
                  style={{
                    width:
                      48,
                    border:
                      0,
                    borderRadius:
                      10,
                    background:
                      '#d4af37',
                    color:
                      '#080808',
                    cursor:
                      'pointer',
                    display:
                      'flex',
                    alignItems:
                      'center',
                    justifyContent:
                      'center'
                  }}
                >
                  <Send
                    size={18}
                  />
                </button>
              </form>
            ) : (
              <p
                style={{
                  textAlign:
                    'center',
                  color:
                    '#777',
                  margin:
                    '12px 0 0'
                }}
              >
                🔒 Connectez-vous pour participer au chat.
              </p>
            )}
          </Panel>
        )}

        {/* ================================================== */}
        {/* RÉACTIONS                                        */}
        {/* ================================================== */}

        {activePanel ===
          'reactions' && (
          <Panel>
            <PanelHeader
              icon={
                <Smile />
              }
              title="Réactions"
              onClose={() =>
                setActivePanel(
                  null
                )
              }
            />

            <div
              style={{
                display:
                  'grid',
                gridTemplateColumns:
                  'repeat(4,1fr)',
                gap:
                  10,
                opacity:
                  currentUser ? 1 : .45,
                pointerEvents:
                  currentUser ? 'auto' : 'none'
              }}
            >
              <ReactionButton
                emoji="🔥"
                value={
                  reactions.fire
                }
                onClick={() =>
                  addReaction(
                    'fire'
                  )
                }
              />

              <ReactionButton
                emoji="❤️"
                value={
                  reactions.heart
                }
                onClick={() =>
                  addReaction(
                    'heart'
                  )
                }
              />

              <ReactionButton
                emoji="😍"
                value={
                  reactions.love
                }
                onClick={() =>
                  addReaction(
                    'love'
                  )
                }
              />

              <ReactionButton
                emoji="🎸"
                value={
                  reactions.music
                }
                onClick={() =>
                  addReaction(
                    'music'
                  )
                }
              />
            </div>

            {!currentUser && (
              <p
                style={{
                  textAlign:
                    'center',
                  color:
                    '#777',
                  margin:
                    '12px 0 0'
                }}
              >
                🔒 Connectez-vous pour réagir.
              </p>
            )}
          </Panel>
        )}

        {/* ================================================== */}
        {/* MINUTEUR                                         */}
        {/* ================================================== */}

        {activePanel ===
          'sleep' && (
          <Panel>
            <PanelHeader
              icon={
                <Moon />
              }
              title="Minuteur"
              onClose={() =>
                setActivePanel(
                  null
                )
              }
            />

            {sleepTimer ? (
              <div
                style={{
                  textAlign:
                    'center'
                }}
              >
                <div
                  style={{
                    marginBottom:
                      15,
                    color:
                      '#d4af37',
                    fontSize:
                      '2rem',
                    fontWeight:
                      900
                  }}
                >
                  🌙{' '}
                  {formatSleepTime(
                    sleepTimeRemaining
                  )}
                </div>

                <button
                  type="button"
                  onClick={
                    cancelSleepTimer
                  }
                  style={{
                    padding:
                      '11px 18px',
                    border:
                      0,
                    borderRadius:
                      10,
                    background:
                      '#c41620',
                    color:
                      '#fff',
                    cursor:
                      'pointer'
                  }}
                >
                  Annuler
                </button>
              </div>
            ) : (
              <div
                style={{
                  display:
                    'grid',
                  gridTemplateColumns:
                    'repeat(3,1fr)',
                  gap:
                    10
                }}
              >
                {[15, 30, 60].map(
                  minutes => (
                    <button
                      key={
                        minutes
                      }
                      type="button"
                      onClick={() =>
                        startSleepTimer(
                          minutes
                        )
                      }
                      style={{
                        padding:
                          15,
                        borderRadius:
                          11,
                        border:
                          '1px solid rgba(212,175,55,.25)',
                        background:
                          '#181818',
                        color:
                          '#fff',
                        cursor:
                          'pointer'
                      }}
                    >
                      {minutes} min
                    </button>
                  )
                )}
              </div>
            )}
          </Panel>
        )}

        {/* ================================================== */}
        {/* PROFIL                                           */}
        {/* ================================================== */}

        {activePanel ===
          'profile' && (
          <Panel>
            <PanelHeader
              icon={
                <Award />
              }
              title="Votre profil"
              onClose={() =>
                setActivePanel(
                  null
                )
              }
            />

            {!currentUser && (
              <p
                style={{
                  textAlign:
                    'center',
                  color:
                    '#777',
                  margin:
                    '0 0 16px'
                }}
              >
                🔒 Connectez-vous pour suivre vos statistiques.
              </p>
            )}

            <div
              style={{
                display:
                  'grid',
                gridTemplateColumns:
                  'repeat(3,1fr)',
                gap:
                  10
              }}
            >
              <ProfileStat
                icon={
                  <Clock />
                }
                value={formatTotalTime(
                  userProfile.totalListenTime
                )}
                label="Temps total"
              />

              <ProfileStat
                icon={
                  <Flame />
                }
                value={
                  userProfile.streak
                }
                label="Streak"
              />

              <ProfileStat
                icon={
                  <Music />
                }
                value={
                  userProfile.sessionsCount
                }
                label="Sessions"
              />
            </div>

            <div
              style={{
                marginTop:
                  18
              }}
            >
              <h3
                style={{
                  margin:
                    '0 0 10px',
                  color:
                    '#d4af37',
                  fontSize:
                    '.9rem'
                }}
              >
                🏆 Vos badges
              </h3>

              {userProfile.badges
                .length ===
              0 ? (
                <p
                  style={{
                    color:
                      '#777'
                  }}
                >
                  Continuez à écouter pour débloquer des badges.
                </p>
              ) : (
                <div
                  style={{
                    display:
                      'flex',
                    flexWrap:
                      'wrap',
                    gap:
                      8
                  }}
                >
                  {userProfile.badges.map(
                    badge => (
                      <span
                        key={
                          badge
                        }
                        style={{
                          padding:
                            '8px 12px',
                          borderRadius:
                            999,
                          background:
                            'rgba(212,175,55,.1)',
                          border:
                            '1px solid rgba(212,175,55,.2)',
                          color:
                            '#d4af37',
                          fontSize:
                            '.78rem'
                        }}
                      >
                        {badge}
                      </span>
                    )
                  )}
                </div>
              )}
            </div>
          </Panel>
        )}

        {/* ================================================== */}
        {/* JOHNNY / CALENDRIER                              */}
        {/* ================================================== */}

        {activePanel ===
          'calendar' && (
          <Panel>
            <PanelHeader
              icon={
                <Calendar />
              }
              title="Johnny Hallyday"
              onClose={() =>
                setActivePanel(
                  null
                )
              }
            />

            <div
              style={{
                display:
                  'grid',
                gap:
                  10
              }}
            >
              <EventRow
                date="15 juin 1943"
                text="🎂 Naissance de Johnny"
              />

              <EventRow
                date="10 juin 2000"
                text="🎸 Concert Tour Eiffel"
              />

              <EventRow
                date="9 décembre 2017"
                text="🕊️ Hommage national"
              />
            </div>
          </Panel>
        )}

      </div>

      {/* ================================================== */}
      {/* RÉACTIONS FLOTTANTES                              */}
      {/* ================================================== */}

      <div
        style={{
          position:
            'fixed',
          inset:
            0,
          pointerEvents:
            'none',
          overflow:
            'hidden',
          zIndex:
            9999
        }}
      >
        {floatingEmojis.map(
          item => (
            <div
              key={
                item.id
              }
              className="floating-reaction"
              style={{
                position:
                  'absolute',
                left:
                  `${item.left}%`,
                bottom:
                  '-40px',
                fontSize:
                  '2.7rem'
              }}
            >
              {item.emoji}
            </div>
          )
        )}
        <p className="rp-version">{VERSION_LABEL}</p>
      </div>

      {/* ================================================== */}
      {/* CSS                                               */}
      {/* ================================================== */}

      <style>{`
        @keyframes livePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: .45; transform: scale(.78); }
        }

        @keyframes reactionUp {
          0% { opacity: 0; transform: translateY(0) scale(.7); }
          15% { opacity: 1; }
          100% { opacity: 0; transform: translateY(-240px) scale(1.25); }
        }

        @keyframes rpRing {
          0% { transform: scale(1); opacity: .55; }
          100% { transform: scale(1.45); opacity: 0; }
        }

        .live-dot {
          width: 9px; height: 9px; display: block; border-radius: 50%;
          background: #fff; animation: livePulse 1.2s ease-in-out infinite;
        }

        .floating-reaction { animation: reactionUp 2.2s ease-out forwards; }

        /* ---------- Page ---------- */
        .rp {
          min-height: 100dvh;
          box-sizing: border-box;
          background: radial-gradient(120% 60% at 50% 0%, #1c1810 0%, #0c0b0a 55%);
          color: #f5f1e8;
          padding: 14px 18px var(--j-page-bottom, 150px);
        }

        .rp-inner {
          width: min(560px, 100%);
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .rp-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          min-height: 40px;
        }

        .rp-live {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 8px 14px; border-radius: 999px;
          background: #c41620; color: #fff;
          font-size: .82rem; font-weight: 800;
        }

        .rp-session {
          display: inline-flex; align-items: center; gap: 6px;
          color: #a39e92; font-size: .9rem; font-variant-numeric: tabular-nums;
        }

        /* ---------- Lecteur centré ---------- */
        .rp-hero {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 8px 0 4px;
        }

        .rp-cover {
          width: min(68vw, 290px);
          aspect-ratio: 1;
          border-radius: 28px;
          overflow: hidden;
          background: #050505;
          box-shadow: 0 24px 60px rgba(0,0,0,.6);
          outline: 1px solid rgba(212,175,55,.22);
          transition: transform .5s var(--j-ease, ease);
        }

        .rp-hero:not(.is-playing) .rp-cover { transform: scale(.94); }

        .rp-cover img { width: 100%; height: 100%; object-fit: cover; display: block; }

        .rp-cover__empty {
          width: 100%; height: 100%;
          display: flex; align-items: center; justify-content: center;
          background: radial-gradient(circle, #2a2618, #070605);
        }

        .rp-title {
          margin: 26px 0 0;
          font-size: 1.85rem;
          line-height: 1.15;
          font-weight: 800;
          letter-spacing: -.02em;
          overflow-wrap: anywhere;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .rp-artist {
          margin: 8px 0 0;
          color: #d4af37;
          font-size: 1.1rem;
          font-weight: 600;
        }

        /* Le bouton lecture : grand, centré, impossible à rater */
        .rp-play {
          position: relative;
          width: 112px;
          height: 112px;
          margin-top: 28px;
          border: 0;
          border-radius: 50%;
          background: radial-gradient(circle at 35% 30%, #f0d77a, #d4af37 58%, #a8862a);
          color: #16140f;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 16px 40px rgba(212,175,55,.35);
          touch-action: manipulation;
          transition: transform .18s ease;
        }

        .rp-play:active { transform: scale(.94); }

        .rp-hero.is-playing .rp-play::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 2px solid #d4af37;
          animation: rpRing 2s ease-out infinite;
        }

        .rp-play-hint {
          margin-top: 12px;
          color: #a39e92;
          font-size: .95rem;
        }

        /* Volume */
        .rp-volume {
          width: 100%;
          max-width: 400px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 26px;
        }

        .rp-volume__mute {
          width: 48px; height: 48px; flex-shrink: 0;
          border: 0; border-radius: 50%;
          background: rgba(255,255,255,.06);
          color: #d8d2c4;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
        }

        .rp-volume__val {
          width: 48px; text-align: right;
          color: #a39e92; font-size: .95rem; font-variant-numeric: tabular-nums;
        }

        .rp-range {
          flex: 1;
          -webkit-appearance: none;
          appearance: none;
          height: 8px;
          border-radius: 999px;
          background: linear-gradient(to right, #d4af37 var(--val), rgba(255,255,255,.14) var(--val));
          outline: none;
        }

        .rp-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 26px; height: 26px; border-radius: 50%;
          background: #f5f1e8; border: 4px solid #d4af37;
          box-shadow: 0 2px 8px rgba(0,0,0,.4);
        }

        .rp-range::-moz-range-thumb {
          width: 20px; height: 20px; border-radius: 50%;
          background: #f5f1e8; border: 4px solid #d4af37;
        }

        /* Votes / partage */
        .rp-votes {
          width: 100%;
          display: grid;
          grid-template-columns: 1fr 1fr 1.4fr;
          gap: 10px;
          margin-top: 22px;
        }

        .rp-chip {
          min-height: 52px;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          padding: 0 12px;
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,.1);
          background: #17150f;
          color: #f5f1e8;
          font-size: 1rem; font-weight: 700;
          cursor: pointer;
          touch-action: manipulation;
        }

        .rp-chip.is-active {
          border-color: rgba(212,175,55,.6);
          background: rgba(212,175,55,.14);
          color: #f0d77a;
        }

        .rp-chip:disabled { cursor: default; }

        /* Actions */
        .rp-actions {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 8px;
        }

        .rp-action {
          position: relative;
          border: 0;
          background: none;
          color: #d8d2c4;
          display: flex; flex-direction: column; align-items: center; gap: 8px;
          padding: 4px 0;
          font-size: .82rem; font-weight: 600;
          cursor: pointer;
          touch-action: manipulation;
        }

        .rp-action__icon {
          width: 58px; height: 58px;
          border-radius: 20px;
          display: flex; align-items: center; justify-content: center;
          background: #17150f;
          border: 1px solid rgba(255,255,255,.08);
          transition: background .2s ease, border-color .2s ease, transform .15s ease;
        }

        .rp-action__icon svg { width: 24px; height: 24px; }
        .rp-action:active .rp-action__icon { transform: scale(.92); }

        .rp-action.is-active { color: #f0d77a; }
        .rp-action.is-active .rp-action__icon {
          background: rgba(212,175,55,.16);
          border-color: rgba(212,175,55,.6);
          color: #f0d77a;
        }

        .rp-action__badge {
          position: absolute;
          top: -2px;
          right: calc(50% - 36px);
          min-width: 22px; height: 22px; padding: 0 6px;
          border-radius: 999px;
          background: #c41620; color: #fff;
          font-size: .7rem; font-weight: 800;
          display: flex; align-items: center; justify-content: center;
          border: 2px solid #0c0b0a;
        }

        /* Panneaux (chat, minuteur…) : plus d'air */
        .radio-panel {
          margin-top: 0 !important;
          padding: 20px !important;
          border-radius: 22px !important;
          background: #16140f !important;
          border: 1px solid rgba(212,175,55,.16) !important;
        }

        .radio-panel h2 { font-size: 1.2rem !important; }

        .radio-panel input,
        .radio-panel textarea { font-size: 16px !important; }

        .radio-panel button { min-height: 44px; }

        .rp button:focus-visible,
        .rp input:focus-visible {
          outline: 3px solid #d4af37;
          outline-offset: 3px;
        }

        .rp-version {
          margin: 8px 0 0;
          text-align: center;
          color: #6f6a60;
          font-size: .78rem;
        }

        .rp * { scrollbar-width: none; }
        .rp *::-webkit-scrollbar { display: none; }

        @media (max-width: 360px) {
          .rp { padding-left: 14px; padding-right: 14px; }
          .rp-play { width: 100px; height: 100px; }
          .rp-title { font-size: 1.6rem; }
          .rp-action__icon { width: 52px; height: 52px; border-radius: 18px; }
          .rp-votes { grid-template-columns: 1fr 1fr; }
          .rp-votes > :last-child { grid-column: 1 / -1; }
        }
      `}</style>
    </div>
  )
}

// ============================================================
// BOUTON DU MENU
// ============================================================

function ActionButton({
  active,
  icon,
  label,
  badge,
  onClick
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rp-action ${active ? 'is-active' : ''}`}
      aria-pressed={Boolean(active)}
    >
      <span className="rp-action__icon">{icon}</span>
      {label}
      {badge ? (
        <span className="rp-action__badge">{badge > 99 ? '99+' : badge}</span>
      ) : null}
    </button>
  )
}

// ============================================================
// BOUTON TRACK
// ============================================================

function TrackButton({
  children,
  active,
  disabled,
  onClick,
  label
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      className={`rp-chip ${active ? 'is-active' : ''}`}
    >
      {children}
    </button>
  )
}

// ============================================================
// PANNEAU
// ============================================================

function Panel({
  children
}) {
  return (
    <section
      className="radio-panel"
      style={{
        marginTop:
          14,
        padding:
          18,
        borderRadius:
          18,
        background:
          '#111',
        border:
          '1px solid rgba(255,255,255,.08)'
      }}
    >
      {children}
    </section>
  )
}

// ============================================================
// TITRE PANNEAU
// ============================================================

function PanelHeader({
  icon,
  title,
  onClose
}) {
  return (
    <div
      style={{
        display:
          'flex',
        alignItems:
          'center',
        justifyContent:
          'space-between',
        marginBottom:
          15
      }}
    >
      <h2
        style={{
          display:
            'flex',
          alignItems:
            'center',
          gap:
            9,
          margin:
            0,
          fontSize:
            '1.05rem'
        }}
      >
        <span
          style={{
            color:
              '#d4af37'
          }}
        >
          {icon}
        </span>

        {title}
      </h2>

      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        style={{
          width:
            44,
          height:
            44,
          borderRadius:
            '50%',
          border:
            '1px solid rgba(255,255,255,.1)',
          background:
            '#181818',
          color:
            '#aaa',
          cursor:
            'pointer',
          display:
            'flex',
          alignItems:
            'center',
          justifyContent:
            'center'
        }}
      >
        <X
          size={17}
        />
      </button>
    </div>
  )
}

// ============================================================
// RÉACTION
// ============================================================

function ReactionButton({
  emoji,
  value,
  onClick
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minHeight:
          85,
        borderRadius:
          14,
        border:
          '1px solid rgba(255,255,255,.1)',
        background:
          '#181818',
        color:
          '#fff',
        cursor:
          'pointer',
        fontSize:
          '1.7rem',
        display:
          'flex',
        flexDirection:
          'column',
        alignItems:
          'center',
        justifyContent:
          'center',
        gap:
          5
      }}
    >
      <span>
        {emoji}
      </span>

      <small
        style={{
          color:
            '#777',
          fontSize:
            '.72rem'
        }}
      >
        {value || 0}
      </small>
    </button>
  )
}

// ============================================================
// STAT PROFIL
// ============================================================

function ProfileStat({
  icon,
  value,
  label
}) {
  return (
    <div
      style={{
        padding:
          14,
        borderRadius:
          13,
        background:
          '#181818',
        textAlign:
          'center'
      }}
    >
      <div
        style={{
          color:
            '#d4af37',
          display:
            'flex',
          justifyContent:
            'center',
          marginBottom:
            7
        }}
      >
        {icon}
      </div>

      <strong
        style={{
          display:
            'block',
          color:
            '#fff'
        }}
      >
        {value}
      </strong>

      <small
        style={{
          display:
            'block',
          marginTop:
            4,
          color:
            '#777'
        }}
      >
        {label}
      </small>
    </div>
  )
}

// ============================================================
// ÉVÉNEMENT
// ============================================================

function EventRow({
  date,
  text
}) {
  return (
    <div
      style={{
        display:
          'flex',
        alignItems:
          'center',
        justifyContent:
          'space-between',
        gap:
          15,
        flexWrap:
          'wrap',
        padding:
          '13px 14px',
        borderRadius:
          12,
        background:
          '#181818'
      }}
    >
      <strong
        style={{
          color:
            '#d4af37',
          fontSize:
            '.82rem'
        }}
      >
        {date}
      </strong>

      <span
        style={{
          color:
            '#ccc',
          fontSize:
            '.82rem'
        }}
      >
        {text}
      </span>
    </div>
  )
}

// ============================================================
// UN SEUL EXPORT DEFAULT — NE PAS EN AJOUTER UN AUTRE
// ============================================================

export default RadioPage